import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiClock,
  FiDollarSign,
  FiPlus,
  FiSave,
  FiTarget,
  FiTrash2,
  FiUserCheck,
  FiZap,
} from 'react-icons/fi';
import Sidebar from '../Sidebar';
import Editor from '../Editor';
import ReviewWorkspace from '../review/ReviewWorkspace';
import VersionHistoryPanel from '../review/VersionHistoryPanel';
import { API_BASE_URL, getAuthHeaders } from '../../utils/utils';
import { getStoredUser } from '../../utils/authSession';
import {
  createFundingSource,
  deleteFundingSource,
  evaluateFundingSource,
  fetchFundingHome,
  fetchFundingReviewers,
  reviewFundingDocument,
  saveFundingDocument,
  saveFundingProfile,
  updateFundingSource,
} from './fundingApi';
import {
  defaultSourceForLender,
  describeLenderWindow,
  getPlaybook,
  hasBorrowerIdentity,
  IDENTITY_COUNTRIES,
  matchLenders,
} from './fundingPlaybooks';
import './Funding.css';

const pickCommentReview = (document) => {
  const reviews = document?.reviews || [];
  return (
    reviews.find((review) => review.reviewer_type === 'notes') ||
    reviews.find((review) => review.reviewer_type === 'coach') ||
    reviews[0] ||
    null
  );
};

const htmlToPlainText = (value = '') =>
  String(value)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();

const EDITOR_STYLES = {
  wrapper: {
    minHeight: '120px',
    width: '100%',
    border: '1px solid var(--border, #e0e0e0)',
    borderRadius: '12px',
    background: '#fff',
  },
  editorContent: `
    .ap-editor .tiptap {
      min-height: 120px !important;
      max-height: 360px;
      height: auto !important;
      color: var(--foreground, #1d1b20);
    }
  `,
};

const SOURCE_KINDS = [
  { value: 'scholarship', label: 'Scholarship' },
  { value: 'loan', label: 'Loan' },
  { value: 'savings', label: 'Savings plan' },
  { value: 'family', label: 'Family' },
  { value: 'employer', label: 'Employer' },
  { value: 'internship', label: 'Internship' },
];

const STUDIO_TABS = [
  { id: 'strategy', label: 'Strategy' },
  { id: 'sources', label: 'Sources' },
  { id: 'dossiers', label: 'Dossiers' },
];

const SOURCE_ORIGINS = [
  { value: 'web', label: 'Found on the web' },
  { value: 'school', label: 'School page' },
  { value: 'counselor', label: 'Liwaza counselor' },
];

const SOURCE_STATUSES = [
  { value: 'researching', label: 'Researching' },
  { value: 'evaluating', label: 'Evaluating' },
  { value: 'working', label: 'Working' },
  { value: 'applied', label: 'Applied' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
  { value: 'approved', label: 'Approved' },
];

const DOCUMENT_KINDS = [
  { value: 'funding_story', label: 'Funding story' },
  { value: 'scholarship_essay', label: 'Scholarship essay' },
  { value: 'employer_pitch', label: 'Employer pitch' },
  { value: 'appeal_letter', label: 'School appeal / negotiation' },
  { value: 'loan_package', label: 'Loan package' },
];

const emptySourceForm = () => ({
  kind: 'scholarship',
  origin: 'web',
  status: 'researching',
  name: '',
  url: '',
  announced_amount: '',
  deadline: '',
  target_id: '',
  criteria_notes: '',
});

const emptyDraft = (kind = 'funding_story') => ({
  kind,
  title: '',
  body: '',
  documentId: null,
  sourceId: '',
  targetId: '',
});

const emptyProfileForm = () => ({
  savings_amount: '',
  family_contribution: '',
  monthly_income: '',
  monthly_savings: '',
  employer_sponsorship: '',
  existing_debt: '',
  currency: 'USD',
  entry_date: '',
  notes: '',
  nationality: '',
  country_of_residence: '',
  marital_status: '',
  has_children: '',
});

function formatMoney(amount, currency = 'USD') {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
    return '—';
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch {
    return `${Number(amount).toFixed(0)} ${currency || ''}`.trim();
  }
}

function formatRange(low, high, currency = 'USD') {
  if (low == null && high == null) {
    return '—';
  }
  if (low == null || high == null || Number(low) === Number(high)) {
    return formatMoney(high ?? low, currency);
  }
  return `${formatMoney(low, currency)} – ${formatMoney(high, currency)}`;
}

function countryChoices(saved) {
  return IDENTITY_COUNTRIES.includes(saved)
    ? IDENTITY_COUNTRIES
    : [saved, ...IDENTITY_COUNTRIES].filter(Boolean);
}

function labelFor(options, value) {
  return options.find((row) => row.value === value)?.label || value;
}

function toNumberOrNull(value) {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  const number = Number(value);
  return Number.isNaN(number) ? null : number;
}

const Funding = () => {
  const user = getStoredUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialKind = DOCUMENT_KINDS.some((kind) => kind.value === searchParams.get('kind'))
    ? searchParams.get('kind')
    : 'funding_story';

  const [home, setHome] = useState(null);
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [profileForm, setProfileForm] = useState(emptyProfileForm);
  const [costOverrides, setCostOverrides] = useState({});
  const [sourceForm, setSourceForm] = useState(() => ({
    ...emptySourceForm(),
    target_id: searchParams.get('target_id') || '',
  }));
  const [activeSourceId, setActiveSourceId] = useState(null);
  const [draft, setDraft] = useState(() => ({
    ...emptyDraft(initialKind),
    targetId: searchParams.get('target_id') || '',
  }));
  const [coachId, setCoachId] = useState('');
  const [commentsOpen, setCommentsOpen] = useState(
    () => Boolean(searchParams.get('comment_id'))
  );
  const [pendingExcerpt, setPendingExcerpt] = useState('');
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [focusedTargetId, setFocusedTargetId] = useState(
    () => Number(searchParams.get('target_id') || 0) || null
  );
  const [studioTab, setStudioTab] = useState(() => {
    if (searchParams.get('document_id') || searchParams.get('kind')) {
      return 'dossiers';
    }
    if (searchParams.get('source_id')) {
      return 'sources';
    }
    return 'strategy';
  });
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [addSourceOpen, setAddSourceOpen] = useState(false);
  const [costEditOpen, setCostEditOpen] = useState(false);
  const [identityOpen, setIdentityOpen] = useState(false);
  const [interviewOpen, setInterviewOpen] = useState(false);
  const [moreDetailsOpen, setMoreDetailsOpen] = useState(false);
  const [remainingNotice, setRemainingNotice] = useState(null);
  const [planOverrides, setPlanOverrides] = useState({});
  const [editingSlice, setEditingSlice] = useState(null);
  const [sliceDraft, setSliceDraft] = useState('');
  const reviewsRef = useRef(null);
  const historyRef = useRef(null);

  const load = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const [homeData, coachesData] = await Promise.all([
        fetchFundingHome(),
        fetchFundingReviewers().catch(() => ({ coaches: [] })),
      ]);
      setHome(homeData);
      setReviewers(coachesData.coaches || []);
      const profile = homeData.profile || {};
      setProfileForm({
        savings_amount: profile.savings_amount ?? '',
        family_contribution: profile.family_contribution ?? '',
        monthly_income: profile.monthly_income ?? '',
        monthly_savings: profile.monthly_savings ?? '',
        employer_sponsorship: profile.employer_sponsorship ?? '',
        existing_debt: profile.existing_debt ?? '',
        currency: profile.currency || 'USD',
        entry_date: profile.entry_date || '',
        notes: profile.notes || '',
        nationality:
          profile.nationality || homeData.identity?.nationality || '',
        country_of_residence:
          profile.country_of_residence ||
          homeData.identity?.country_of_residence ||
          '',
        marital_status:
          profile.marital_status || homeData.identity?.marital_status || '',
        has_children:
          profile.has_children === true || homeData.identity?.has_children === true
            ? 'yes'
            : profile.has_children === false || homeData.identity?.has_children === false
              ? 'no'
              : '',
      });
      setCostOverrides(profile.cost_overrides || {});
      setPlanOverrides(profile.plan_overrides || {});
      setInterviewOpen(!homeData.complete);
      return homeData;
    } catch (err) {
      setError(err.message || 'Could not load funding studio');
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const kind = searchParams.get('kind');
    if (DOCUMENT_KINDS.some((row) => row.value === kind)) {
      setDraft((prev) => (prev.documentId ? prev : { ...prev, kind }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (!home) {
      return;
    }
    const documentId = Number(searchParams.get('document_id') || 0);
    const sourceId = Number(searchParams.get('source_id') || 0);
    const targetId = Number(searchParams.get('target_id') || 0);
    if (sourceId) {
      setActiveSourceId(sourceId);
      setStudioTab('sources');
    }
    if (documentId) {
      const document = (home.documents || []).find((row) => row.id === documentId);
      if (document) {
        setDraft({
          kind: document.kind,
          title: document.title,
          body: document.body || '',
          documentId: document.id,
          sourceId: document.source_id || '',
          targetId: document.target_id || '',
        });
        if (document.target_id) {
          setFocusedTargetId(document.target_id);
        }
        if (searchParams.get('comment_id')) {
          setCommentsOpen(true);
        }
        setStudioTab('dossiers');
        return;
      }
    }
    if (targetId && (home.targets || []).some((row) => row.id === targetId)) {
      setFocusedTargetId(targetId);
      setSourceForm((prev) => ({ ...prev, target_id: String(targetId) }));
      setDraft((prev) =>
        prev.documentId ? prev : { ...prev, targetId: String(targetId) }
      );
    }
  }, [home, searchParams]);

  const targets = home?.targets || [];
  const sources = home?.sources || [];
  const documents = home?.documents || [];
  const mix = home?.mix || [];
  const activeTargetId = focusedTargetId || targets[0]?.id || null;
  const focusedTarget =
    targets.find((row) => row.id === activeTargetId) || null;
  const activeMix =
    mix.find((row) => row.id === activeTargetId) || mix[0] || null;
  const toolbarCommentCount = useMemo(() => {
    if (!draft.documentId) {
      return 0;
    }
    const document = documents.find((row) => row.id === draft.documentId);
    return (document?.reviews || []).reduce(
      (count, review) => count + (review.comments || []).length,
      0
    );
  }, [documents, draft.documentId]);

  const currentDocumentHasReviews = Boolean(
    draft.documentId &&
      documents.some((doc) => doc.id === draft.documentId && doc.reviews?.length)
  );

  const openComments = (payload = {}) => {
    const excerpt = String(payload?.excerpt || '').replace(/\s+/g, ' ').trim();
    if (excerpt) {
      setPendingExcerpt(excerpt);
    }
    setCommentsOpen(true);
    if (
      currentDocumentHasReviews &&
      reviewsRef.current &&
      typeof reviewsRef.current.scrollIntoView === 'function'
    ) {
      setError('');
      reviewsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (currentDocumentHasReviews) {
      setError('');
      return;
    }
    setError('Request a Liwaza AI or coach review to start commenting.');
  };
  const activeSource = useMemo(
    () => sources.find((row) => row.id === activeSourceId) || sources[0] || null,
    [sources, activeSourceId]
  );

  const saveProfile = async () => {
    setSaving(true);
    setError('');
    try {
      await saveFundingProfile({
        savings_amount: toNumberOrNull(profileForm.savings_amount),
        family_contribution: toNumberOrNull(profileForm.family_contribution),
        monthly_income: toNumberOrNull(profileForm.monthly_income),
        monthly_savings: toNumberOrNull(profileForm.monthly_savings),
        employer_sponsorship: toNumberOrNull(profileForm.employer_sponsorship),
        existing_debt: toNumberOrNull(profileForm.existing_debt),
        currency: profileForm.currency || 'USD',
        entry_date: profileForm.entry_date || null,
        notes: profileForm.notes || null,
        nationality: profileForm.nationality || null,
        country_of_residence: profileForm.country_of_residence || null,
        marital_status: profileForm.marital_status || null,
        has_children:
          profileForm.has_children === 'yes'
            ? true
            : profileForm.has_children === 'no'
              ? false
              : null,
        cost_overrides: Object.fromEntries(
          Object.entries(costOverrides).map(([key, row]) => [
            key,
            {
              tuition: toNumberOrNull(row?.tuition),
              living: toNumberOrNull(row?.living),
              currency: row?.currency || profileForm.currency || 'USD',
            },
          ])
        ),
        plan_overrides: Object.fromEntries(
          Object.entries(planOverrides).map(([key, row]) => [
            key,
            {
              cash: toNumberOrNull(row?.cash),
              scholarship: toNumberOrNull(row?.scholarship),
              loan: toNumberOrNull(row?.loan),
            },
          ])
        ),
      });
      const homeData = await load();
      if (homeData?.complete) {
        setInterviewOpen(false);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveInterview = async () => {
    if (
      !hasBorrowerIdentity(profileForm.nationality, profileForm.country_of_residence)
    ) {
      setError('Add your nationality and the country you live in now.');
      return;
    }
    if (!profileForm.marital_status) {
      setError('Tell us if you are married. It changes school-aid odds.');
      return;
    }
    if (profileForm.has_children !== 'yes' && profileForm.has_children !== 'no') {
      setError('Tell us if you have children. It changes school-aid odds.');
      return;
    }
    await saveProfile();
  };

  const persistPlanOverrides = async (nextOverrides, extraProfile = {}) => {
    setPlanOverrides(nextOverrides);
    setSaving(true);
    setError('');
    try {
      await saveFundingProfile({
        family_contribution:
          extraProfile.family_contribution !== undefined
            ? extraProfile.family_contribution
            : toNumberOrNull(profileForm.family_contribution),
        plan_overrides: Object.fromEntries(
          Object.entries(nextOverrides).map(([key, row]) => [
            key,
            {
              cash: toNumberOrNull(row?.cash),
              scholarship: toNumberOrNull(row?.scholarship),
              loan: toNumberOrNull(row?.loan),
            },
          ])
        ),
      });
      await load({ silent: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addSource = async () => {
    if (!sourceForm.name.trim()) {
      setError('Give the source a name (the award, lender, or pot you found).');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const previousRemaining = remainingNotice?.remaining ?? activeMix?.strategy?.remaining ?? activeMix?.gap;
      const data = await createFundingSource({
        kind: sourceForm.kind,
        origin: sourceForm.origin,
        status: sourceForm.status,
        name: sourceForm.name.trim(),
        url: sourceForm.url || null,
        announced_amount: toNumberOrNull(sourceForm.announced_amount),
        deadline: sourceForm.deadline || null,
        target_id: sourceForm.target_id ? Number(sourceForm.target_id) : null,
        criteria_notes: sourceForm.criteria_notes || null,
      });
      setSourceForm({
        ...emptySourceForm(),
        target_id: sourceForm.target_id || '',
      });
      setActiveSourceId(data.source?.id || null);
      setAddSourceOpen(false);
      setStudioTab('strategy');
      if (data.remaining != null) {
        setRemainingNotice({
          remaining: data.remaining,
          previous: previousRemaining,
          currency: data.currency || schoolCurrency,
          name: data.source?.name,
        });
      }
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const changeSourceStatus = async (source, status) => {
    setSaving(true);
    try {
      await updateFundingSource(source.id, { status });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeSource = async (source) => {
    setSaving(true);
    try {
      await deleteFundingSource(source.id);
      if (activeSourceId === source.id) {
        setActiveSourceId(null);
      }
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const evaluateSource = async (source) => {
    setSaving(true);
    setError('');
    try {
      await evaluateFundingSource(source.id, { reviewer: 'ai' });
      setActiveSourceId(source.id);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const persistDocument = async () => {
    const nextTitle = draft.title.trim() || 'Untitled draft';
    const data = await saveFundingDocument({
      kind: draft.kind,
      title: nextTitle,
      body: draft.body,
      document_id: draft.documentId,
      source_id: draft.sourceId ? Number(draft.sourceId) : null,
      target_id: draft.targetId ? Number(draft.targetId) : null,
    });
    setDraft((prev) => ({
      ...prev,
      title: nextTitle,
      documentId: data.document.id,
    }));
    await loadFundingVersions(data.document.id);
    return data.document.id;
  };

  const loadFundingVersions = async (documentId) => {
    if (!documentId) {
      setVersions([]);
      return [];
    }
    setVersionsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/funding/documents/${documentId}/versions`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      const rows = data.versions || [];
      setVersions(rows);
      return rows;
    } catch (_err) {
      setVersions([]);
      return [];
    } finally {
      setVersionsLoading(false);
    }
  };

  useEffect(() => {
    loadFundingVersions(draft.documentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.documentId]);

  const postFundingReviewAction = async (path, payload) => {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: payload ? JSON.stringify(payload) : undefined,
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Could not save comment');
    }
    if (data.document?.body != null) {
      setDraft((prev) => ({
        ...prev,
        body: data.document.body,
        title: data.document.title || prev.title,
      }));
    }
    await load({ silent: true });
    return data;
  };

  const saveDocument = async () => {
    if (!draft.title.trim()) {
      setError('Give the document a title');
      return null;
    }
    setSaving(true);
    setError('');
    try {
      const documentId = await persistDocument();
      await load({ silent: true });
      return documentId;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const submitInlineComment = async (payload) => {
    if (!htmlToPlainText(draft.body)) {
      setError('Write or paste a document before commenting');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const documentId = await persistDocument();
      const homeData = await load({ silent: true });
      const document = (homeData?.documents || []).find((row) => row.id === documentId);
      let review = pickCommentReview(document);
      if (!review) {
        const data = await reviewFundingDocument(documentId, { reviewer: 'notes' });
        review = data.review;
      }
      const response = await fetch(
        `${API_BASE_URL}/api/funding/review-requests/${review.id}/comments`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            excerpt: payload.excerpt,
            comment: payload.comment,
            start_offset: payload.start_offset,
            end_offset: payload.end_offset,
            assigned_user_id: payload.assigned_user_id,
            suggestion_text: payload.suggestion_text,
            mentioned_user_ids: payload.mentioned_user_ids,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Could not save comment');
      }
      await load({ silent: true });
      setCommentsOpen(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const requestReview = async (reviewer) => {
    let documentId = draft.documentId;
    if (!documentId) {
      if (!draft.title.trim() || !htmlToPlainText(draft.body)) {
        setError('Write or paste a document before requesting a review');
        return;
      }
      documentId = await saveDocument();
    }
    if (!documentId) return;
    if (reviewer === 'coach' && !coachId) {
      setError('Select a review coach');
      return;
    }
    setSaving(true);
    try {
      await reviewFundingDocument(documentId, {
        reviewer,
        coach_id: reviewer === 'coach' ? Number(coachId) : undefined,
      });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openDocument = (document) => {
    setDraft({
      kind: document.kind,
      title: document.title,
      body: document.body || '',
      documentId: document.id,
      sourceId: document.source_id || '',
      targetId: document.target_id || '',
    });
    if (document.reviews?.length) {
      setError('');
    }
  };

  const startArtifact = (kind, source) => {
    setDraft({
      kind,
      title: source ? `${labelFor(DOCUMENT_KINDS, kind)} · ${source.name}` : '',
      body: '',
      documentId: null,
      sourceId: source?.id || '',
      targetId: source?.target_id || focusedTarget?.id || '',
    });
    setStudioTab('dossiers');
    const work = document.getElementById('funding-work');
    if (work) {
      work.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const selectTarget = (id) => {
    setFocusedTargetId(id);
    setSourceForm((prev) => ({ ...prev, target_id: String(id) }));
    setDraft((prev) =>
      prev.documentId ? prev : { ...prev, targetId: String(id) }
    );
  };

  const openPrefillSource = (partial) => {
    setSourceForm({
      ...emptySourceForm(),
      ...partial,
      target_id: partial.target_id || (activeTargetId ? String(activeTargetId) : ''),
    });
    setAddSourceOpen(true);
  };

  const handleMove = (action) => {
    if (action.target_id) {
      setFocusedTargetId(action.target_id);
    }
    const href = action.href || '';
    if (href === 'interview' || action.id === 'complete_profile') {
      setStudioTab('strategy');
      setInterviewOpen(true);
      return;
    }
    if (href === 'snapshot') {
      setStudioTab('strategy');
      setSnapshotOpen(true);
      return;
    }
    if (
      href === 'sources' ||
      action.id === 'add_first_source' ||
      action.id === 'evaluate_sources' ||
      action.id === 'file_africa_awards'
    ) {
      setStudioTab('sources');
      if (action.id === 'add_first_source') {
        setAddSourceOpen(true);
      }
      return;
    }
    if (href === 'dossiers' || action.id === 'write_story') {
      startArtifact('funding_story', null);
      return;
    }
    setStudioTab('strategy');
  };

  const currency = profileForm.currency || home?.profile?.currency || 'USD';
  const playbookCountry =
    activeMix?.cost?.country || focusedTarget?.program?.country || '';
  const playbook = getPlaybook(playbookCountry, activeMix?.cost?.currency || currency);
  const schoolCost = Number(activeMix?.cost?.total || 0);
  const schoolCurrency = activeMix?.cost?.currency || activeMix?.strategy?.currency || currency;
  const strategy = activeMix?.strategy || (home?.strategy || []).find((row) => row.target_id === activeTargetId);
  const targetPlan = planOverrides[String(activeTargetId)] || {};
  const planned = {
    cash:
      targetPlan.cash ??
      strategy?.planned?.cash ??
      strategy?.cash_certain ??
      toNumberOrNull(profileForm.family_contribution) ??
      0,
    scholarship: targetPlan.scholarship ?? strategy?.planned?.scholarship ?? strategy?.aid?.likely ?? 0,
    loan: targetPlan.loan ?? strategy?.planned?.loan ?? strategy?.loan_guidance ?? 0,
  };
  const remaining =
    schoolCost -
    Number(planned.cash || 0) -
    Number(planned.scholarship || 0) -
    Number(planned.loan || 0);

  const remainingForTarget = (targetId, row) => {
    const override = planOverrides[String(targetId)] || {};
    const rowStrategy = row?.strategy;
    const cost = Number(row?.cost?.total || 0);
    const cash = override.cash ?? rowStrategy?.planned?.cash ?? rowStrategy?.cash_certain ?? 0;
    const scholarship =
      override.scholarship ?? rowStrategy?.planned?.scholarship ?? rowStrategy?.aid?.likely ?? 0;
    const loan = override.loan ?? rowStrategy?.planned?.loan ?? rowStrategy?.loan_guidance ?? 0;
    return cost - Number(cash || 0) - Number(scholarship || 0) - Number(loan || 0);
  };

  const beginEditSlice = (field) => {
    setEditingSlice(field);
    setSliceDraft(String(planned[field] ?? ''));
  };

  const commitEditSlice = async () => {
    if (!editingSlice || !activeTargetId) {
      setEditingSlice(null);
      return;
    }
    const amount = toNumberOrNull(sliceDraft) ?? 0;
    const next = {
      ...planOverrides,
      [String(activeTargetId)]: {
        cash: planned.cash,
        scholarship: planned.scholarship,
        loan: planned.loan,
        [editingSlice]: amount,
      },
    };
    setEditingSlice(null);
    if (editingSlice === 'cash') {
      setProfileForm((prev) => ({ ...prev, family_contribution: sliceDraft }));
    }
    await persistPlanOverrides(
      next,
      editingSlice === 'cash' ? { family_contribution: amount } : {}
    );
  };
  const interviewComplete = Boolean(home?.complete);
  const knownIdentity = hasBorrowerIdentity(
    profileForm.nationality,
    profileForm.country_of_residence
  );
  const lenderRows = matchLenders(
    playbook.id,
    profileForm.nationality,
    profileForm.country_of_residence
  );
  const loanTarget = strategy?.loan_guidance || 0;
  const nextActions = strategy?.next_actions || home?.next_actions || [];
  const huntPrompts = strategy?.hunt_prompts || home?.hunt_prompts || [];

  return (
    <div className="funding-layout">
      <Sidebar />
      <main className="funding-content">
        <header className="funding-header-container">
          <nav className="funding-breadcrumb">
            <button
              type="button"
              className="funding-back"
              onClick={() => navigate('/home#targeting')}
            >
              <FiArrowLeft size={16} />
              Update school targets
            </button>
          </nav>
          <h1>
            <FiDollarSign className="funding-header-icon" />
            {user?.first_name ? `${user.first_name}, ` : ''}fund your graduate studies
          </h1>
          <p className="text-muted">
            {focusedTarget
              ? `Funding plan for ${focusedTarget.program?.school_name || 'this school'}${
                  focusedTarget.program?.name ? ` · ${focusedTarget.program.name}` : ''
                }. Build and stress-test your funding mix.`
              : 'Answer a few questions about you. We turn that into a funding plan for each school — aid as a range, cash you already have, and what is still left to pay.'}
          </p>
        </header>

        {loading ? <p>Loading your funding studio…</p> : null}

        {!loading && !targets.length ? (
          <div className="funding-empty">
            <FiTarget size={40} />
            <h3>Choose 3 school targets first</h3>
            <p>
              Costs and negotiation are tied to the programs you are applying to.
            </p>
            <button
              type="button"
              className="btn-lwz-primary"
              onClick={() => navigate('/home#targeting')}
            >
              Choose targets on Home
            </button>
          </div>
        ) : null}

        {!loading && targets.length ? (
          <div className="funding-studio">
            <aside className="funding-targets" aria-label="Target schools">
              {targets.map((target) => {
                const row = mix.find((item) => item.id === target.id);
                const region = getPlaybook(
                  row?.cost?.country || target.program?.country,
                  row?.cost?.currency || currency
                );
                return (
                  <button
                    key={target.id}
                    type="button"
                    className={target.id === activeTargetId ? 'is-active' : ''}
                    onClick={() => selectTarget(target.id)}
                  >
                    <strong>
                      #{target.priority} {target.program?.school_name}
                    </strong>
                    <span>{target.program?.name}</span>
                    <em>
                      {row
                        ? remainingForTarget(target.id, row) <= 0
                          ? 'Covered on this plan'
                          : `Still ${formatMoney(
                              remainingForTarget(target.id, row),
                              row.cost?.currency || row.strategy?.currency || currency
                            )}`
                        : 'Add costs'}
                    </em>
                    <span className="funding-region">{region.label}</span>
                  </button>
                );
              })}
            </aside>

            <div className="funding-workspace">
              {interviewOpen || !interviewComplete ? (
                <form
                  className={`funding-interview${interviewComplete ? ' is-editing' : ''}`}
                  onSubmit={(event) => {
                    event.preventDefault();
                    saveInterview();
                  }}
                >
                  <p className="funding-kicker">Who you are</p>
                  <h2>
                    {interviewComplete
                      ? 'Update your answers'
                      : 'A few questions, then a funding plan'}
                  </h2>
                  <p>
                    Nationality and family change school-aid odds and which lenders
                    fit. Cash is what you already have. You will not pick a
                    scholarship amount — the school decides that.
                  </p>
                  <div className="funding-identity-grid">
                    <label>
                      Nationality (passport)
                      <select
                        value={profileForm.nationality}
                        onChange={(event) =>
                          setProfileForm((prev) => ({
                            ...prev,
                            nationality: event.target.value,
                          }))
                        }
                        required
                      >
                        <option value="">Select nationality</option>
                        {countryChoices(profileForm.nationality).map((country) => (
                          <option key={`nat-${country}`} value={country}>
                            {country}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Country you live in now
                      <select
                        value={profileForm.country_of_residence}
                        onChange={(event) =>
                          setProfileForm((prev) => ({
                            ...prev,
                            country_of_residence: event.target.value,
                          }))
                        }
                        required
                      >
                        <option value="">Select country</option>
                        {countryChoices(profileForm.country_of_residence).map((country) => (
                          <option key={`res-${country}`} value={country}>
                            {country}
                          </option>
                        ))}
                      </select>
                    </label>
                    <fieldset className="funding-choice">
                      <legend>Are you married?</legend>
                      <div className="funding-choice-options">
                        {[
                          ['married', 'Yes'],
                          ['single', 'No'],
                        ].map(([value, label]) => (
                          <label key={value}>
                            <input
                              type="radio"
                              name="marital_status"
                              checked={profileForm.marital_status === value}
                              onChange={() =>
                                setProfileForm((prev) => ({
                                  ...prev,
                                  marital_status: value,
                                }))
                              }
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    <fieldset className="funding-choice">
                      <legend>Do you have children?</legend>
                      <div className="funding-choice-options">
                        {[
                          ['yes', 'Yes'],
                          ['no', 'No'],
                        ].map(([value, label]) => (
                          <label key={value}>
                            <input
                              type="radio"
                              name="has_children"
                              checked={profileForm.has_children === value}
                              onChange={() =>
                                setProfileForm((prev) => ({
                                  ...prev,
                                  has_children: value,
                                }))
                              }
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    <label>
                      Cash you and your family can put down
                      <input
                        type="number"
                        min="0"
                        value={profileForm.family_contribution}
                        onChange={(event) =>
                          setProfileForm((prev) => ({
                            ...prev,
                            family_contribution: event.target.value,
                          }))
                        }
                        placeholder="0 is OK"
                      />
                    </label>
                    <label>
                      Employer help
                      <input
                        type="number"
                        min="0"
                        value={profileForm.employer_sponsorship}
                        onChange={(event) =>
                          setProfileForm((prev) => ({
                            ...prev,
                            employer_sponsorship: event.target.value,
                          }))
                        }
                        placeholder="Leave empty if none"
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    className="funding-ghost"
                    onClick={() => setMoreDetailsOpen((open) => !open)}
                  >
                    {moreDetailsOpen ? 'Hide extra details' : 'Monthly income and entry date'}
                  </button>
                  {moreDetailsOpen ? (
                    <div className="funding-identity-grid">
                      <label>
                        Monthly income
                        <input
                          type="number"
                          value={profileForm.monthly_income}
                          onChange={(event) =>
                            setProfileForm((prev) => ({
                              ...prev,
                              monthly_income: event.target.value,
                            }))
                          }
                        />
                      </label>
                      <label>
                        Target entry date
                        <input
                          type="date"
                          value={profileForm.entry_date}
                          onChange={(event) =>
                            setProfileForm((prev) => ({
                              ...prev,
                              entry_date: event.target.value,
                            }))
                          }
                        />
                      </label>
                    </div>
                  ) : null}
                  <div className="funding-interview-actions">
                    {interviewComplete ? (
                      <button
                        type="button"
                        className="btn-lwz-outline"
                        onClick={() => setInterviewOpen(false)}
                      >
                        Cancel
                      </button>
                    ) : null}
                    <button type="submit" className="btn-lwz-primary" disabled={saving}>
                      <FiSave />{' '}
                      {saving
                        ? 'Saving…'
                        : interviewComplete
                          ? 'Update answers'
                          : 'Show my funding plan'}
                    </button>
                  </div>
                </form>
              ) : (
              <div className="funding-cash">
                <div className="funding-cash-row">
                  <p className="funding-kicker">Your cash</p>
                  <span className="funding-chip">
                    {formatMoney(strategy?.cash_certain ?? profileForm.family_contribution, schoolCurrency)} ready
                  </span>
                  {profileForm.employer_sponsorship !== '' && profileForm.employer_sponsorship !== null ? (
                    <span className="funding-chip">
                      {formatMoney(profileForm.employer_sponsorship, schoolCurrency)} employer
                    </span>
                  ) : null}
                  <button
                    type="button"
                    className="funding-ghost"
                    onClick={() => setInterviewOpen(true)}
                  >
                    Edit answers
                  </button>
                  <button
                    type="button"
                    className="funding-ghost"
                    onClick={() => setSnapshotOpen((open) => !open)}
                  >
                    {snapshotOpen ? 'Hide snapshot' : 'Edit snapshot'}
                  </button>
                </div>
                {snapshotOpen ? (
                  <div>
                    <div className="funding-form-grid">
                      {[
                        ['savings_amount', 'Current savings'],
                        ['family_contribution', 'Family contribution'],
                        ['monthly_savings', 'Monthly savings'],
                        ['monthly_income', 'Monthly income'],
                        ['employer_sponsorship', 'Employer sponsorship'],
                        ['existing_debt', 'Existing debt'],
                      ].map(([key, label]) => (
                        <label key={key}>
                          {label}
                          <input
                            type="number"
                            value={profileForm[key]}
                            onChange={(event) =>
                              setProfileForm((prev) => ({
                                ...prev,
                                [key]: event.target.value,
                              }))
                            }
                          />
                        </label>
                      ))}
                      <label>
                        Currency
                        <input
                          value={profileForm.currency}
                          onChange={(event) =>
                            setProfileForm((prev) => ({
                              ...prev,
                              currency: event.target.value.toUpperCase(),
                            }))
                          }
                        />
                      </label>
                      <label>
                        Target entry date
                        <input
                          type="date"
                          value={profileForm.entry_date}
                          onChange={(event) =>
                            setProfileForm((prev) => ({
                              ...prev,
                              entry_date: event.target.value,
                            }))
                          }
                        />
                      </label>
                    </div>
                    <label className="funding-notes">
                      Notes
                      <textarea
                        value={profileForm.notes}
                        onChange={(event) =>
                          setProfileForm((prev) => ({ ...prev, notes: event.target.value }))
                        }
                        rows={2}
                      />
                    </label>
                    <button
                      type="button"
                      className="btn-lwz-primary"
                      disabled={saving}
                      onClick={saveProfile}
                    >
                      <FiSave /> {saving ? 'Saving…' : 'Save snapshot and cost overrides'}
                    </button>
                  </div>
                ) : null}
              </div>
              )}

              {interviewComplete ? (
              <>
              <div className="funding-tabs" role="tablist">
                {STUDIO_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={studioTab === tab.id}
                    className={studioTab === tab.id ? 'is-active' : ''}
                    onClick={() => setStudioTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <section hidden={studioTab !== 'strategy'} aria-label="Funding strategy">
                {remainingNotice ? (
                  <div className="funding-remaining-banner" role="status">
                    <strong>
                      Still to pay:{' '}
                      {formatMoney(remainingNotice.remaining, remainingNotice.currency || schoolCurrency)}
                    </strong>
                    <p>
                      {remainingNotice.name ? `${remainingNotice.name} is on the plan. ` : ''}
                      {remainingNotice.previous != null
                        ? `Was ${formatMoney(remainingNotice.previous, remainingNotice.currency || schoolCurrency)}.`
                        : 'Logged sources update this number. The aid range is an outlook, not money yet.'}
                    </p>
                    <button
                      type="button"
                      className="funding-ghost"
                      onClick={() => setRemainingNotice(null)}
                    >
                      Dismiss
                    </button>
                  </div>
                ) : null}
                <dl className="funding-metrics">
                  <div>
                    <dt>School cost</dt>
                    <dd>{formatMoney(schoolCost, schoolCurrency)}</dd>
                  </div>
                  <div>
                    <dt>On this plan</dt>
                    <dd>
                      {formatMoney(
                        Number(planned.cash || 0) +
                          Number(planned.scholarship || 0) +
                          Number(planned.loan || 0),
                        schoolCurrency
                      )}
                    </dd>
                  </div>
                  <div className={remaining <= 0 ? 'is-ok' : 'is-gap'}>
                    <dt>Still to pay</dt>
                    <dd>{formatMoney(Math.max(remaining, 0), schoolCurrency)}</dd>
                  </div>
                </dl>
                <em>
                  {remaining <= 0
                    ? remaining < 0
                      ? `This plan covers the school with ${formatMoney(Math.abs(remaining), schoolCurrency)} extra`
                      : 'This plan covers the school — click a card to change an amount'
                    : 'Click a card to change an amount and see what is still missing'}
                </em>
                <div className="funding-section-head">
                  <p className="funding-cost-meta">
                    {activeMix?.cost?.source === 'override'
                      ? 'Official numbers you entered'
                      : 'Country average — override if you have the school figure'}
                  </p>
                  <button
                    type="button"
                    className="funding-ghost"
                    onClick={() => setCostEditOpen((open) => !open)}
                  >
                    {costEditOpen ? 'Hide cost' : 'Edit cost'}
                  </button>
                </div>
                {costEditOpen && activeTargetId ? (
                  <div className="funding-cost-edit">
                    <label>
                      Tuition
                      <input
                        type="number"
                        value={costOverrides[String(activeTargetId)]?.tuition ?? ''}
                        onChange={(event) =>
                          setCostOverrides((prev) => ({
                            ...prev,
                            [String(activeTargetId)]: {
                              ...(prev[String(activeTargetId)] || {}),
                              tuition: event.target.value,
                              currency: schoolCurrency,
                            },
                          }))
                        }
                      />
                    </label>
                    <label>
                      Living
                      <input
                        type="number"
                        value={costOverrides[String(activeTargetId)]?.living ?? ''}
                        onChange={(event) =>
                          setCostOverrides((prev) => ({
                            ...prev,
                            [String(activeTargetId)]: {
                              ...(prev[String(activeTargetId)] || {}),
                              living: event.target.value,
                              currency: schoolCurrency,
                            },
                          }))
                        }
                      />
                    </label>
                  </div>
                ) : null}

                <p className="funding-kicker">{playbook.label} plan</p>
                <p className="funding-playbook-head">
                  {strategy?.family_boost
                    ? playbook.id === 'us'
                      ? 'Married with children: stronger school-aid outlook on this US program.'
                      : 'Married with children: Europe aid outlook is about half of cost, up to €40k.'
                    : playbook.headline}
                </p>
                <p className="funding-lead">{playbook.applyWindow}</p>

                <div
                  className="funding-mix-bar"
                  role="img"
                  aria-label={`${playbook.label} funding stack`}
                >
                  <span style={{ flex: Math.max(Number(planned.cash) || 1, 1) }} title="Cash" />
                  <span
                    style={{ flex: Math.max(Number(planned.scholarship) || 1, 1) }}
                    title="Scholarship"
                  />
                  <span
                    style={{ flex: Math.max(Number(planned.loan) || 1, 1) }}
                    title="Loan or employer"
                  />
                </div>

                <div className="funding-slices">
                  {[
                    {
                      id: 'cash',
                      label: 'Cash you already have',
                      hint: 'Click to change what you and your family can put down.',
                    },
                    {
                      id: 'scholarship',
                      label: 'Scholarship you expect',
                      hint: strategy?.aid
                        ? `Suggested range ${formatRange(strategy.aid.low, strategy.aid.stretch, schoolCurrency)}. The school decides — set what you plan for.`
                        : 'Set the award you think you can get.',
                      badge: strategy?.family_boost ? 'Family boost' : 'Suggested',
                    },
                    {
                      id: 'loan',
                      label: 'Loan or employer you expect',
                      hint: 'Click to change the loan or sponsorship you plan to use.',
                    },
                  ].map((slice) => (
                    <article
                      key={slice.id}
                      className={editingSlice === slice.id ? 'is-editing' : 'is-editable'}
                    >
                      {editingSlice === slice.id ? (
                        <div className="funding-slice-edit">
                          <strong>
                            {slice.label}
                            {slice.badge ? (
                              <span
                                className={`funding-status ${
                                  strategy?.family_boost && slice.id === 'scholarship'
                                    ? 'is-on_track'
                                    : 'is-short'
                                }`}
                              >
                                {slice.badge}
                              </span>
                            ) : null}
                          </strong>
                          <input
                            type="number"
                            min="0"
                            autoFocus
                            aria-label={slice.label}
                            value={sliceDraft}
                            onChange={(event) => setSliceDraft(event.target.value)}
                            onBlur={commitEditSlice}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                event.currentTarget.blur();
                              }
                              if (event.key === 'Escape') {
                                setEditingSlice(null);
                              }
                            }}
                          />
                          <p>{slice.hint}</p>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="funding-slice-edit"
                          onClick={() => beginEditSlice(slice.id)}
                        >
                          <strong>
                            {slice.label}
                            {slice.badge ? (
                              <span
                                className={`funding-status ${
                                  strategy?.family_boost && slice.id === 'scholarship'
                                    ? 'is-on_track'
                                    : 'is-short'
                                }`}
                              >
                                {slice.badge}
                              </span>
                            ) : null}
                          </strong>
                          <span>{formatMoney(planned[slice.id], schoolCurrency)}</span>
                          <p>{slice.hint}</p>
                        </button>
                      )}
                    </article>
                  ))}
                </div>
                {(strategy?.matched_awards || []).length ? (
                  <div className="funding-africa-awards" aria-label="Matched Africa awards">
                    <p className="funding-kicker">Liwaza-verified Africa awards</p>
                    <p className="funding-lead">
                      Alumni from this community have filed these at{' '}
                      {activeMix?.program?.school_name || 'this school'}. Add the ones
                      you will actually apply to — they are not money yet.
                    </p>
                    <div className="funding-africa-award-list">
                      {strategy.matched_awards.map((award) => (
                        <article key={`${award.id}-${award.target_id || 'any'}`}>
                          <strong>{award.name}</strong>
                          <p>{award.amount_note}</p>
                          <p>{award.eligibility}</p>
                          <button
                            type="button"
                            className="btn-lwz-outline"
                            onClick={() =>
                              openPrefillSource({
                                kind: award.kind || 'scholarship',
                                origin: 'school',
                                name: award.name,
                                url: award.url || '',
                                announced_amount: award.announced_amount
                                  ? String(award.announced_amount)
                                  : '',
                                target_id: award.target_id
                                  ? String(award.target_id)
                                  : activeTargetId
                                    ? String(activeTargetId)
                                    : '',
                                criteria_notes: award.eligibility || '',
                              })
                            }
                          >
                            Add {award.name}
                          </button>
                        </article>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="funding-section-head">
                  <p className="funding-cost-meta">
                    Click a card to change an amount. Still-to-pay updates immediately.
                  </p>
                  <button
                    type="button"
                    className="btn-lwz-primary"
                    onClick={() => {
                      setSourceForm((prev) => ({
                        ...emptySourceForm(),
                        kind: 'scholarship',
                        origin: 'school',
                        target_id: activeTargetId ? String(activeTargetId) : prev.target_id,
                        announced_amount: '',
                      }));
                      setAddSourceOpen(true);
                    }}
                  >
                    <FiPlus /> Log a source
                  </button>
                </div>
                {addSourceOpen && studioTab === 'strategy' ? (
                  <div className="funding-drawer">
                    <p className="funding-lead">
                      Amount is optional on scholarships — only fill it if the page states one.
                    </p>
                    <div className="funding-source-form">
                      <select
                        value={sourceForm.kind}
                        onChange={(event) =>
                          setSourceForm((prev) => ({ ...prev, kind: event.target.value }))
                        }
                      >
                        {SOURCE_KINDS.map((kind) => (
                          <option key={kind.value} value={kind.value}>
                            {kind.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={sourceForm.origin}
                        onChange={(event) =>
                          setSourceForm((prev) => ({ ...prev, origin: event.target.value }))
                        }
                      >
                        {SOURCE_ORIGINS.map((origin) => (
                          <option key={origin.value} value={origin.value}>
                            {origin.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={sourceForm.target_id}
                        onChange={(event) =>
                          setSourceForm((prev) => ({ ...prev, target_id: event.target.value }))
                        }
                      >
                        <option value="">All target schools</option>
                        {targets.map((target) => (
                          <option key={target.id} value={target.id}>
                            {target.program?.school_name} · {target.program?.name}
                          </option>
                        ))}
                      </select>
                      <input
                        value={sourceForm.name}
                        onChange={(event) =>
                          setSourceForm((prev) => ({ ...prev, name: event.target.value }))
                        }
                        placeholder="Name of the award or lender"
                      />
                      <input
                        type="number"
                        value={sourceForm.announced_amount}
                        onChange={(event) =>
                          setSourceForm((prev) => ({
                            ...prev,
                            announced_amount: event.target.value,
                          }))
                        }
                        placeholder="Amount if the page states one"
                      />
                      <input
                        value={sourceForm.url}
                        onChange={(event) =>
                          setSourceForm((prev) => ({ ...prev, url: event.target.value }))
                        }
                        placeholder="Link you found"
                      />
                      <textarea
                        value={sourceForm.criteria_notes}
                        onChange={(event) =>
                          setSourceForm((prev) => ({
                            ...prev,
                            criteria_notes: event.target.value,
                          }))
                        }
                        placeholder="Paste eligibility, nationality rules, or lender terms"
                        rows={3}
                      />
                      <button
                        type="button"
                        className="btn-lwz-primary"
                        disabled={saving}
                        onClick={addSource}
                      >
                        <FiPlus /> Add source
                      </button>
                    </div>
                  </div>
                ) : null}

                {nextActions.length ? (
                  <div className="funding-moves" aria-label="Next actions">
                    <p className="funding-kicker">Next moves</p>
                    {nextActions.map((action) => (
                      <button
                        key={action.id}
                        type="button"
                        onClick={() => handleMove(action)}
                      >
                        <strong>{action.title}</strong>
                        <p>{action.body}</p>
                      </button>
                    ))}
                  </div>
                ) : null}

                <div className="funding-timeline">
                  <p className="funding-kicker">When to move</p>
                  <p>
                    {playbook.id === 'us'
                      ? 'School aid in R1–R2. Lender files in January–May for Fall. Internship pay cannot cover the deposit or I-20.'
                      : 'School aid is decided with the admit. Round 3 scholarships are often already gone.'}
                  </p>
                </div>

                <div className="funding-lenders-head">
                  <p className="funding-kicker">Lenders that fit you</p>
                  {knownIdentity ? (
                    <button
                      type="button"
                      className="funding-ghost"
                      onClick={() => setIdentityOpen((open) => !open)}
                    >
                      {identityOpen ? 'Hide' : 'Change origin'}
                    </button>
                  ) : null}
                </div>
                {knownIdentity && !identityOpen ? (
                  <p className="funding-identity-chip">
                    {profileForm.nationality} passport · lives in{' '}
                    {profileForm.country_of_residence}
                    {`. Matched to ${playbook.label} programs.`}
                  </p>
                ) : (
                  <form
                    className="funding-identity"
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (
                        !hasBorrowerIdentity(
                          profileForm.nationality,
                          profileForm.country_of_residence
                        )
                      ) {
                        setError('Add your nationality and the country you live in now.');
                        return;
                      }
                      setIdentityOpen(false);
                      saveProfile();
                    }}
                  >
                    <p>
                      We only suggest lenders after we know your passport and
                      where you live. Eligibility changes with both.
                    </p>
                    <div className="funding-identity-grid">
                      <label>
                        Nationality (passport)
                        <select
                          value={profileForm.nationality}
                          onChange={(event) =>
                            setProfileForm((prev) => ({
                              ...prev,
                              nationality: event.target.value,
                            }))
                          }
                          required
                        >
                          <option value="">Select nationality</option>
                          {(IDENTITY_COUNTRIES.includes(profileForm.nationality)
                            ? IDENTITY_COUNTRIES
                            : [profileForm.nationality, ...IDENTITY_COUNTRIES].filter(Boolean)
                          ).map((country) => (
                            <option key={`nat-${country}`} value={country}>
                              {country}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Country you live in now
                        <select
                          value={profileForm.country_of_residence}
                          onChange={(event) =>
                            setProfileForm((prev) => ({
                              ...prev,
                              country_of_residence: event.target.value,
                            }))
                          }
                          required
                        >
                          <option value="">Select country</option>
                          {(IDENTITY_COUNTRIES.includes(profileForm.country_of_residence)
                            ? IDENTITY_COUNTRIES
                            : [profileForm.country_of_residence, ...IDENTITY_COUNTRIES].filter(Boolean)
                          ).map((country) => (
                            <option key={`res-${country}`} value={country}>
                              {country}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <button
                      type="submit"
                      className="btn-lwz-primary"
                      disabled={saving}
                    >
                      Show matching lenders
                    </button>
                  </form>
                )}
                {knownIdentity && !identityOpen ? (
                  <>
                    <div className="funding-lenders">
                      {lenderRows.map((lender) => {
                        const windowInfo = describeLenderWindow(lender);
                        return (
                          <article key={lender.id}>
                            <span className={`funding-window is-${windowInfo.status}`}>
                              {windowInfo.headline}
                            </span>
                            <strong>{lender.name}</strong>
                            <p className="funding-lender-who">{windowInfo.who}</p>
                            <dl className="funding-window-facts">
                              <div>
                                <dt>Apply</dt>
                                <dd>{windowInfo.applyWhen}</dd>
                              </div>
                              {windowInfo.avoidWhen ? (
                                <div>
                                  <dt>Usually late</dt>
                                  <dd>{windowInfo.avoidWhen}</dd>
                                </div>
                              ) : null}
                              <div>
                                <dt>This month</dt>
                                <dd>{windowInfo.nowMeaning}</dd>
                              </div>
                            </dl>
                            {lender.kind === 'research' ? (
                              <a href={lender.url} target="_blank" rel="noreferrer">
                                Compare, then add the real lender
                              </a>
                            ) : (
                              <button
                                type="button"
                                className="btn-lwz-outline"
                                onClick={() =>
                                  openPrefillSource(
                                    defaultSourceForLender(
                                      lender,
                                      playbook,
                                      activeTargetId,
                                      loanTarget
                                    )
                                  )
                                }
                              >
                                {lender.kind === 'employer'
                                  ? `Add ${lender.name}`
                                  : `Add ${lender.name}`}
                              </button>
                            )}
                          </article>
                        );
                      })}
                    </div>
                    <p className="funding-disclaimer">
                      Typical windows, not live availability. We do not scrape a
                      catalog — add the award or lender you will actually apply to.
                    </p>
                  </>
                ) : null}

                <p className="funding-kicker">Go find sources</p>
                <div className="funding-hunt-inline">
                  {huntPrompts.map((prompt) => (
                    <article key={prompt.id}>
                      <strong>{prompt.title}</strong>
                      <p>{prompt.body}</p>
                      {prompt.url ? (
                        <a href={prompt.url} target="_blank" rel="noreferrer">
                          Open the page
                        </a>
                      ) : null}
                    </article>
                  ))}
                </div>
              </section>

              <section
                hidden={studioTab !== 'sources'}
                aria-label="Funding pipeline"
              >
              <div className="funding-section-head">
                <h2>Sources you found</h2>
                <button
                  type="button"
                  className="btn-lwz-secondary"
                  onClick={() => setAddSourceOpen((open) => !open)}
                >
                  <FiPlus /> {addSourceOpen ? 'Hide form' : 'Add a source'}
                </button>
              </div>
              {addSourceOpen && studioTab === 'sources' ? (
              <div className="funding-drawer">
              <div className="funding-source-form">
                <select
                  value={sourceForm.kind}
                  onChange={(event) =>
                    setSourceForm((prev) => ({ ...prev, kind: event.target.value }))
                  }
                >
                  {SOURCE_KINDS.map((kind) => (
                    <option key={kind.value} value={kind.value}>
                      {kind.label}
                    </option>
                  ))}
                </select>
                <select
                  value={sourceForm.origin}
                  onChange={(event) =>
                    setSourceForm((prev) => ({ ...prev, origin: event.target.value }))
                  }
                >
                  {SOURCE_ORIGINS.map((origin) => (
                    <option key={origin.value} value={origin.value}>
                      {origin.label}
                    </option>
                  ))}
                </select>
                <select
                  value={sourceForm.target_id}
                  onChange={(event) =>
                    setSourceForm((prev) => ({ ...prev, target_id: event.target.value }))
                  }
                >
                  <option value="">All target schools</option>
                  {targets.map((target) => (
                    <option key={target.id} value={target.id}>
                      {target.program?.school_name} · {target.program?.name}
                    </option>
                  ))}
                </select>
                <input
                  value={sourceForm.name}
                  onChange={(event) =>
                    setSourceForm((prev) => ({ ...prev, name: event.target.value }))
                  }
                  placeholder="Name of the award or lender"
                />
                <input
                  type="number"
                  value={sourceForm.announced_amount}
                  onChange={(event) =>
                    setSourceForm((prev) => ({
                      ...prev,
                      announced_amount: event.target.value,
                    }))
                  }
                  placeholder="Amount if the page states one"
                />
                <input
                  type="date"
                  value={sourceForm.deadline}
                  onChange={(event) =>
                    setSourceForm((prev) => ({ ...prev, deadline: event.target.value }))
                  }
                />
                <input
                  value={sourceForm.url}
                  onChange={(event) =>
                    setSourceForm((prev) => ({ ...prev, url: event.target.value }))
                  }
                  placeholder="Link you found"
                />
                <textarea
                  value={sourceForm.criteria_notes}
                  onChange={(event) =>
                    setSourceForm((prev) => ({
                      ...prev,
                      criteria_notes: event.target.value,
                    }))
                  }
                  placeholder="Paste eligibility, nationality rules, or lender terms"
                  rows={3}
                />
                <button
                  type="button"
                  className="btn-lwz-primary"
                  disabled={saving}
                  onClick={addSource}
                >
                  <FiPlus /> Add source
                </button>
              </div>
              </div>
              ) : null}

              {!sources.length ? (
                <p>No sources yet. Add the first award or loan you found.</p>
              ) : (
                <div className="funding-source-list">
                  {sources.map((source) => {
                    const schoolName = targets.find((row) => row.id === source.target_id)
                      ?.program?.school_name;
                    return (
                    <article
                      key={source.id}
                      className={source.id === activeSource?.id ? 'is-active' : ''}
                    >
                      <button
                        type="button"
                        className="funding-source-main"
                        onClick={() => setActiveSourceId(source.id)}
                      >
                        <span className="funding-source-title">
                          <strong>{source.name}</strong>
                          {source.announced_amount ? (
                            <b>
                              {formatMoney(
                                source.announced_amount,
                                source.currency || currency
                              )}
                            </b>
                          ) : null}
                        </span>
                        <span>
                          {labelFor(SOURCE_KINDS, source.kind)}
                          {schoolName ? ` · ${schoolName}` : ''}
                          {' · '}
                          {labelFor(SOURCE_ORIGINS, source.origin)}
                        </span>
                        {source.evaluation ? (
                          <em>
                            {Math.round((source.evaluation.p_win || 0) * 100)}% · EV{' '}
                            {formatMoney(
                              source.evaluation.expected_value,
                              source.currency || currency
                            )}
                          </em>
                        ) : (
                          <em>Not evaluated yet</em>
                        )}
                      </button>
                      <div className="funding-source-actions">
                        <label>
                          Status
                          <select
                            value={source.status}
                            onChange={(event) =>
                              changeSourceStatus(source, event.target.value)
                            }
                          >
                            {SOURCE_STATUSES.map((status) => (
                              <option key={status.value} value={status.value}>
                                {status.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <button
                          type="button"
                          className="btn-lwz-secondary"
                          disabled={saving}
                          onClick={() => evaluateSource(source)}
                        >
                          Score odds
                        </button>
                        <button
                          type="button"
                          className="funding-icon-btn"
                          onClick={() => removeSource(source)}
                          aria-label={`Remove ${source.name}`}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </article>
                    );
                  })}
                </div>
              )}

              {activeSource?.evaluation ? (
                <div className="funding-eval">
                  <h3>Estimate for {activeSource.name}</h3>
                  <p>{activeSource.evaluation.rationale}</p>
                  <dl>
                    <div>
                      <dt>Chance</dt>
                      <dd>{Math.round((activeSource.evaluation.p_win || 0) * 100)}%</dd>
                    </div>
                    <div>
                      <dt>Likely</dt>
                      <dd>
                        {formatMoney(
                          activeSource.evaluation.amount_likely,
                          activeSource.currency || currency
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Stretch</dt>
                      <dd>
                        {formatMoney(
                          activeSource.evaluation.amount_stretch,
                          activeSource.currency || currency
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Expected value</dt>
                      <dd>
                        {formatMoney(
                          activeSource.evaluation.expected_value,
                          activeSource.currency || currency
                        )}
                      </dd>
                    </div>
                  </dl>
                  {(activeSource.evaluation.blockers || []).length ? (
                    <ul>
                      {activeSource.evaluation.blockers.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  ) : null}
                  {(activeSource.evaluation.levers || []).length ? (
                    <ul>
                      {activeSource.evaluation.levers.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  ) : null}
                  {activeSource.evaluation.next_artifact ? (
                    <button
                      type="button"
                      className="btn-lwz-outline"
                      onClick={() =>
                        startArtifact(activeSource.evaluation.next_artifact, activeSource)
                      }
                    >
                      Work the {labelFor(DOCUMENT_KINDS, activeSource.evaluation.next_artifact)}
                    </button>
                  ) : null}
                </div>
              ) : null}
              </section>

              <section
                id="funding-work"
                hidden={studioTab !== 'dossiers'}
                aria-label="Funding documents"
              >
              <div className="funding-docs-header">
                <h2>Work the dossiers</h2>
                <button
                  type="button"
                  className="funding-ghost"
                  onClick={() => setDraft(emptyDraft(initialKind))}
                >
                  <FiPlus size={14} /> New draft
                </button>
              </div>
              {documents.length ? (
                <div className="funding-doc-chips">
                  {documents.map((doc) => (
                    <button
                      type="button"
                      key={doc.id}
                      className={doc.id === draft.documentId ? 'is-active' : ''}
                      onClick={() => openDocument(doc)}
                    >
                      <strong>{doc.title}</strong>
                      <span>
                        {labelFor(DOCUMENT_KINDS, doc.kind)}
                        {doc.reviews?.length ? ` · ${doc.reviews.length} review(s)` : ''}
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <p>
                  Write your funding story, a scholarship essay, an employer pitch,
                  a loan package, or the appeal letter that asks a school for more aid.
                </p>
              )}

              <div className="funding-editor-row">
                <select
                  value={draft.kind}
                  onChange={(event) =>
                    setDraft((prev) => ({ ...prev, kind: event.target.value }))
                  }
                >
                  {DOCUMENT_KINDS.map((kind) => (
                    <option key={kind.value} value={kind.value}>
                      {kind.label}
                    </option>
                  ))}
                </select>
                <select
                  value={draft.sourceId}
                  onChange={(event) =>
                    setDraft((prev) => ({ ...prev, sourceId: event.target.value }))
                  }
                >
                  <option value="">No linked source</option>
                  {sources.map((source) => (
                    <option key={source.id} value={source.id}>
                      {source.name}
                    </option>
                  ))}
                </select>
                <select
                  value={draft.targetId}
                  onChange={(event) =>
                    setDraft((prev) => ({ ...prev, targetId: event.target.value }))
                  }
                >
                  <option value="">All schools</option>
                  {targets.map((target) => (
                    <option key={target.id} value={target.id}>
                      {target.program?.school_name}
                    </option>
                  ))}
                </select>
                <input
                  value={draft.title}
                  onChange={(event) =>
                    setDraft((prev) => ({ ...prev, title: event.target.value }))
                  }
                  placeholder="Document title"
                />
              </div>
              <Editor
                value={draft.body}
                onChange={(body) => setDraft((prev) => ({ ...prev, body }))}
                placeholder="Write here or paste a draft for correction."
                customStyles={EDITOR_STYLES}
                showToolbar
                onComment={openComments}
                onAddComment={submitInlineComment}
                commentCount={toolbarCommentCount}
                commentsActive={commentsOpen}
                commentSaving={saving}
              />
              <div className="funding-editor-actions">
                <div className="funding-editor-toolbar">
                  <button
                    type="button"
                    className="btn-lwz-primary"
                    disabled={saving}
                    onClick={saveDocument}
                  >
                    <FiSave /> {saving ? 'Saving…' : 'Save draft'}
                  </button>
                  <button
                    type="button"
                    className={`funding-history-btn${historyOpen ? ' is-active' : ''}`}
                    aria-pressed={historyOpen}
                    onClick={async () => {
                      const next = !historyOpen;
                      setHistoryOpen(next);
                      if (next) {
                        await loadFundingVersions(draft.documentId);
                        historyRef.current?.scrollIntoView?.({
                          behavior: 'smooth',
                          block: 'start',
                        });
                      }
                    }}
                  >
                    <FiClock /> Version history
                  </button>
                </div>
                {historyOpen ? (
                  <div ref={historyRef}>
                    <VersionHistoryPanel
                      versions={versions}
                      loading={versionsLoading}
                      currentTitle={draft.title}
                      currentBody={draft.body}
                      saving={saving}
                      onClose={() => setHistoryOpen(false)}
                      onRestore={async (versionId) => {
                        if (!draft.documentId) {
                          setError('Save the draft before restoring a version');
                          return;
                        }
                        try {
                          await postFundingReviewAction(
                            `/api/funding/documents/${draft.documentId}/versions/${versionId}/restore`
                          );
                          await loadFundingVersions(draft.documentId);
                        } catch (err) {
                          setError(err.message);
                        }
                      }}
                    />
                  </div>
                ) : null}
                <div className="funding-review-actions">
                  <p className="funding-review-actions__label">Get a review</p>
                  <div className="funding-review-actions__grid">
                    <button
                      type="button"
                      className="btn-lwz-secondary"
                      disabled={saving}
                      aria-label="Request Liwaza AI review"
                      onClick={() => requestReview('ai')}
                    >
                      <FiZap size={16} /> Ask Liwaza AI
                    </button>
                    <div className="funding-coach-action">
                      <select
                        value={coachId}
                        aria-label="Select a review coach"
                        onChange={(event) => setCoachId(event.target.value)}
                      >
                        <option value="">Select a review coach</option>
                        {reviewers.map((coach) => (
                          <option key={coach.id} value={coach.id}>
                            {coach.name || `Coach #${coach.id}`}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn-lwz-outline"
                        disabled={saving || !coachId}
                        aria-label="Request coach review"
                        title={
                          coachId
                            ? 'Send this draft to the selected coach'
                            : 'Select a review coach'
                        }
                        onClick={() => requestReview('coach')}
                      >
                        <FiUserCheck size={16} /> Ask coach
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {documents.some((doc) => doc.reviews?.length) ? (
                <div className="funding-reviews" ref={reviewsRef} id="funding-reviews">
                  <h3>Reviews</h3>
                  {documents.map((doc) => {
                    const comments = (doc.reviews || []).flatMap((review) =>
                      (review.comments || []).map((comment) => ({
                        ...comment,
                        review_id: review.id,
                      }))
                    );
                    const reviewId =
                      (doc.reviews || []).find((review) => review.reviewer_type === 'coach')?.id ||
                      (doc.reviews || [])[0]?.id;
                    return (
                      <article key={doc.id} className="funding-review">
                        <header>
                          <strong>{doc.title}</strong>
                        </header>
                        {comments.length ? (
                          <ReviewWorkspace
                            body={doc.body || draft.body}
                            comments={comments}
                            selectedCommentId={Number(searchParams.get('comment_id') || 0) || null}
                            focusComments={commentsOpen}
                            pendingExcerpt={pendingExcerpt}
                            canComment={Boolean(reviewId)}
                            layout="stack"
                            saving={saving}
                            error={error}
                            currentUserId={user?.id}
                            collaborators={(doc.reviews || []).find((row) => row.id === reviewId)?.collaborators || []}
                            versions={versions}
                            versionsLoading={versionsLoading}
                            onSubmitComment={async (payload) => {
                              const targetReview = payload.review_id || reviewId;
                              try {
                                await postFundingReviewAction(
                                  `/api/funding/review-requests/${targetReview}/comments`,
                                  {
                                    excerpt: payload.excerpt,
                                    comment: payload.comment,
                                    parent_id: payload.parent_id,
                                    start_offset: payload.start_offset,
                                    end_offset: payload.end_offset,
                                    assigned_user_id: payload.assigned_user_id,
                                    suggestion_text: payload.suggestion_text,
                                    mentioned_user_ids: payload.mentioned_user_ids,
                                  }
                                );
                              } catch (err) {
                                setError(err.message);
                              }
                            }}
                            onResolve={(commentId) =>
                              postFundingReviewAction(
                                `/api/funding/review-requests/${reviewId}/comments/${commentId}/resolve`
                              )
                            }
                            onReopen={(commentId) =>
                              postFundingReviewAction(
                                `/api/funding/review-requests/${reviewId}/comments/${commentId}/reopen`
                              )
                            }
                            onReact={(commentId, emoji) =>
                              postFundingReviewAction(
                                `/api/funding/review-requests/${reviewId}/comments/${commentId}/react`,
                                { emoji }
                              )
                            }
                            onAssign={(commentId, assignedUserId) =>
                              postFundingReviewAction(
                                `/api/funding/review-requests/${reviewId}/comments/${commentId}/assign`,
                                { assigned_user_id: assignedUserId }
                              )
                            }
                            onAcceptSuggestion={(commentId) =>
                              postFundingReviewAction(
                                `/api/funding/review-requests/${reviewId}/comments/${commentId}/accept-suggestion`
                              )
                            }
                            onRejectSuggestion={(commentId) =>
                              postFundingReviewAction(
                                `/api/funding/review-requests/${reviewId}/comments/${commentId}/reject-suggestion`
                              )
                            }
                            onRestoreVersion={async (versionId) => {
                              if (!draft.documentId) {
                                return;
                              }
                              await postFundingReviewAction(
                                `/api/funding/documents/${draft.documentId}/versions/${versionId}/restore`
                              );
                              await loadFundingVersions(draft.documentId);
                            }}
                          />
                        ) : (
                          <p>Comments will appear here as soon as the coach writes them.</p>
                        )}
                      </article>
                    );
                  })}
                </div>
              ) : null}
              </section>
              </>
              ) : null}
            </div>
          </div>
        ) : null}

        {error ? <p className="funding-error">{error}</p> : null}
      </main>
    </div>
  );
};

export default Funding;
